import React from 'react';
import { Button, FormInstance, TableProps, Tag } from 'antd';

import { ValidateAccessEligibilityResult } from '@app/domain-bundle-interfaces';

import { useEligibilityTag, useValidateAccessEligibility } from '../../bundles';
import { SelectAccessTourStepProps } from '../forms/inputs';
import { EligibilityPopover } from '../modals';

export const useSubscriptionSelectAccess = (bundleId: number, requesterEnterpriseId: string, form?: FormInstance) => {
  const { getProps: getTagProps } = useEligibilityTag();
  const tagRef = React.useRef<HTMLElement>(null);
  const { data, isFetching } = useValidateAccessEligibility({
    bundleId,
    requesterEnterpriseId,
  });
  const selectAccessTourStepProps: SelectAccessTourStepProps = {
    title: 'Click for info on selecting access and eligibility',
    steps: [
      {
        title: 'Select Accesses',
        description: (
          <>
            <p>
              Users select items from the bundle to subscribe to. Subscribing to an item does not guarantee access. SAAF
              will request each item selected from the system of origin on your behalf.
            </p>
            <p>
              Subscribers should request access to only the bundle items needed to perform their work. User can select
              item per item or all the enabled for the specific person by clicking in the top selection box.
            </p>
            <p>
              At least one item must be selected.If you add an access with dependencies, you will be asked to add the
              dependencies or cancel. If you remove an access with dependents, you will be asked to remove the
              dependents or cancel.
            </p>
          </>
        ),
      },
      {
        title: 'Eligibility',
        description: (
          <>
            <p>
              According to the worker and bundle selected, the application will enable or disable the bundle items based
              on validations. These tags indicate whether the worker is eligible for the item or not.{' '}
              <strong>To view detailed validation results, hover over or click on the eligibility result tag.</strong>
            </p>
            <p>
              Since the validations are related to DMS, product family, DIE, process, etc, the best channel to ensure if
              they really should have access to this specific item is to revisit with the CW BB Sponsor or Manager.
            </p>
            <Button
              type="link"
              style={{ paddingLeft: 0 }}
              onClick={() =>
                window.open('https://wiki.ith.intel.com/pages/viewpage.action?pageId=3040657977', '_blank')
              }
            >
              Click for more information on Intel Worker validations.
            </Button>
            <Button
              type="link"
              style={{ paddingLeft: 0 }}
              onClick={() =>
                window.open('https://wiki.ith.intel.com/pages/viewpage.action?pageId=3485306786', '_blank')
              }
            >
              Click for more information on Contingent Worker validations.
            </Button>
          </>
        ),
        target: () => tagRef.current as HTMLElement,
      },
    ],
  };

  const getSelectAccessTableProps: TableProps<ValidateAccessEligibilityResult> = {
    columns: [
      {
        title: 'Eligibility',
        key: 'eligibility',
        render: (_, record, index) =>
          record.messages &&
          typeof record.isEligible === 'boolean' && (
            <EligibilityPopover messages={record.messages}>
              <Tag ref={index === 0 ? tagRef : undefined} {...getTagProps(record.isEligible)} />
            </EligibilityPopover>
          ),
      },
    ],
    dataSource: data,
    loading: isFetching,
    rowSelection: {
      getCheckboxProps: (record: ValidateAccessEligibilityResult) => ({
        disabled: !record.isEligible, // Column configuration not to be checked
      }),
    },
  };
  return { getSelectAccessTableProps, selectAccessTourStepProps };
};
